import type { MemoryConfig } from '@timelyapp/integration-cloud-api-spec/src/models/models.memory_config';
import { getEnvironment } from '@timelyapp/integration-cloud-sdk/api/api.environment.utils';
import {
  DefaultDemoMemorySettings,
  type DemoMemorySettings,
  DemoMemorySettingsSchema,
} from '@timelyapp/integration-cloud-sdk/memory-demo-data/types';
import { getCurrentAccount } from 'redux/selectors/accounts';
import { getIsUserInExperimentalFeatureGroup } from 'redux/selectors/user';
import { getAccountOwner } from 'redux/selectors/users';
import { createSelector } from 'reselect';
import debug from 'utils/debug';
import { getMemoryConfig } from './tic.memories.config.selectors';

// TODO: we probably want to use a feature flag instead of hardcoded account and user IDs.
const INITIAL_DEMO_MODE_ACCOUNT_IDS = [
  1098397, // Orvar Testing Stuff in prod
];
const OWNER_USER_IDS_PRODUCTION = [
  37, // Mathias
  1042261, // Orvar
  2292744, // demo@timely.com
];
const OWNER_USER_IDS_STAGING = [
  60789, // Orvar
];
export const getDemoModeAvailability = createSelector(
  [getCurrentAccount, getIsUserInExperimentalFeatureGroup, getAccountOwner],
  (account, isExperimental, accountOwner) => {
    const isHardcodedAccount = INITIAL_DEMO_MODE_ACCOUNT_IDS.includes(account.id);
    const accountHasDemoInName = /\bdemo\b/i.test(account.name);
    const env = getEnvironment();
    const userIds = env === 'production' ? OWNER_USER_IDS_PRODUCTION : OWNER_USER_IDS_STAGING;
    const isDemoAccount =
      accountHasDemoInName && userIds.some(userId => userId === account.owner_id);

    // Check if workspace owner has @timely.com or @timelyapp.com email
    const isQualifyingWorkspace =
      accountOwner?.email &&
      (accountOwner.email.endsWith('@timely.com') || accountOwner.email.endsWith('@timelyapp.com'));

    if (isHardcodedAccount || isDemoAccount || isQualifyingWorkspace) {
      return 'enabled-by-default';
    }
    if (isExperimental) {
      return 'available';
    }
    return 'disabled';
  }
);

export const getTicViewDemoMode = createSelector(
  [getDemoModeAvailability, getMemoryConfig],
  (availability, memoryConfig) => {
    if (availability === 'disabled') {
      return {
        ...DefaultDemoMemorySettings,
        enabled: false,
      };
    }
    const settings = getTicDemoModeFromMemoryConfig(memoryConfig);
    if (settings) {
      // User has saved settings, use them
      return settings;
    }
    if (availability === 'available') {
      // Available but not enabled by default
      return DefaultDemoMemorySettings;
    }
    // availability is enabled-by-default - enable by default but only if no saved settings
    return {
      ...DefaultDemoMemorySettings,
      enabled: true,
    };
  }
);

function getTicDemoModeFromMemoryConfig(memoryConfig: MemoryConfig): DemoMemorySettings | null {
  const demoSettings = memoryConfig.demoSettings as any;
  if (!demoSettings) {
    // No settings saved yet
    return null;
  }

  // The demoSettings from memoryConfig should already be properly typed
  const parsedDemoMode = DemoMemorySettingsSchema.safeParse(demoSettings);
  if (parsedDemoMode.success) {
    return parsedDemoMode.data;
  } else {
    debug.template.devEnvOnly(
      'Invalid demo mode settings in memory config, falling back to defaults',
      parsedDemoMode.error
    );
  }
  return DefaultDemoMemorySettings;
}
